import { Box, Typography } from "@mui/material";
import BreakdownBars from "../components/BreakdownBars";
import Card, { CardGrid } from "../components/Card";
import Heatmap from "../components/Heatmap";
import KpiTile from "../components/KpiTile";
import SeriesChart from "../components/SeriesChart";
import StatTable from "../components/StatTable";
import StatsGate from "../components/StatsGate";
import { formatNumber, formatPercent } from "../format";
import { Chat, Reactions, TabProps } from "../types";
import { useStats } from "../useStats";

export default function ChatReactionsTab({ query, reloadKey, onOpenGame }: TabProps & {
  onOpenGame: (tenant: string, gameId: string) => void;
}) {
  const chat = useStats<Chat>("/chat", query, reloadKey);
  const reactions = useStats<Reactions>("/reactions", query, reloadKey);
  return (
    <>
      <Typography variant="h6" sx={{ mb: 1 }}>Chat</Typography>
      <StatsGate state={chat}>
        {(data, envelope) => (
          <>
            <Box sx={{ display: "grid", gap: 2, gridTemplateColumns: { xs: "1fr", sm: "repeat(5, 1fr)" }, mb: 2 }}>
              <KpiTile label="Messages" value={data.totals.messages} />
              <KpiTile label="Chatters" value={data.totals.chatters} />
              <KpiTile label="Messages per chatter" value={data.totals.avgMessagesPerChatter} format={formatNumber} />
              <KpiTile label="Replies" value={data.totals.replyRate} format={formatPercent} />
              <KpiTile label="Edited" value={data.totals.editRate} format={formatPercent} />
            </Box>
            <CardGrid>
              <Card title="Messages and chatters" wide>
                <SeriesChart points={data.series} bucket={envelope.filters.bucket ?? "day"}
                  lines={[{ key: "messages", label: "Messages" }, { key: "chatters", label: "Chatters" }]} />
              </Card>
              <Card title="When fans chat (your time)" wide><Heatmap cells={data.heatmap} unit="messages" /></Card>
              <Card title="By game period">
                <BreakdownBars rows={data.byPeriod} label={(k) => (k === null ? "No period" : `Period ${k}`)} />
              </Card>
              <Card title="Message status"><BreakdownBars rows={data.byStatus} /></Card>
              <Card title="Moderation state"><BreakdownBars rows={data.byModerationState} /></Card>
              <Card title="Busiest games" wide>
                <StatTable
                  rows={data.topGames} getRowId={(r) => `${r.tenant}:${r.gameId}`}
                  onRowClick={(r) => onOpenGame(r.tenant, r.gameId)}
                  columns={[
                    { field: "tenant", headerName: "Tenant", flex: 1 },
                    { field: "gameId", headerName: "Game", flex: 1 },
                    { field: "messages", headerName: "Messages", type: "number", flex: 1 },
                    { field: "chatters", headerName: "Chatters", type: "number", flex: 1 },
                  ]}
                />
              </Card>
              <Card title="Top chatters" wide>
                <StatTable
                  rows={data.topChatters} getRowId={(r) => r.userId}
                  columns={[
                    { field: "displayName", headerName: "Fan", flex: 2 },
                    { field: "tenant", headerName: "Tenant", flex: 1 },
                    { field: "messages", headerName: "Messages", type: "number", flex: 1 },
                  ]}
                />
              </Card>
            </CardGrid>
          </>
        )}
      </StatsGate>

      <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>Reactions</Typography>
      <StatsGate state={reactions}>
        {(data, envelope) => (
          <CardGrid>
            <Card title="Reactions over time" wide>
              <SeriesChart points={data.series} bucket={envelope.filters.bucket ?? "day"}
                lines={[{ key: "quickReactions", label: "Quick reactions" }, { key: "messageReactions", label: "On messages" }]} />
            </Card>
            <Card title="Quick reactions by type"><BreakdownBars rows={data.quickByType} /></Card>
            <Card title="Message reactions by type"><BreakdownBars rows={data.messageByType} /></Card>
            <Card title="Most reacted messages" wide>
              <StatTable
                rows={data.topReactedMessages} getRowId={(r) => r.messageId}
                columns={[
                  { field: "bodyPreview", headerName: "Message", flex: 3 },
                  { field: "tenant", headerName: "Tenant", flex: 1 },
                  { field: "gameId", headerName: "Game", flex: 1 },
                  { field: "reactions", headerName: "Reactions", type: "number", flex: 1 },
                ]}
              />
            </Card>
          </CardGrid>
        )}
      </StatsGate>
    </>
  );
}
